import * as React from 'react';
import DatePicker, { ReactDatePickerProps } from 'react-datepicker';
import { IMask } from 'react-imask';

import { Input, MaskedInputProps } from '@/ui-kit/input/input';
import { Icon } from '@/ui-kit/icon/icon';

import { useNativeInput } from '@/forms/hooks';

import 'react-datepicker/dist/react-datepicker.css';
import './date-selector.css';
import { parseDateFromString } from '@/forms/utils';

interface DatePickerProps extends Omit<ReactDatePickerProps, 'onChange'> {
  onChange?: ReactDatePickerProps['onChange'];
  onInputChange?: React.ChangeEventHandler<HTMLInputElement>;
  label: MaskedInputProps['label'];
  state?: MaskedInputProps['state'];
  stateText?: MaskedInputProps['stateText'];
  stateDefaultIcon?: MaskedInputProps['stateDefaultIcon'];
}

const DateSelector = React.forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      selected,
      onChange,
      label,
      state,
      stateText,
      stateDefaultIcon,
      name,
      onInputChange,
      value,
      onBlur,
      ...props
    },
    forwardedRef,
  ) => {
    const ref = React.useRef<HTMLInputElement>(null);
    const hiddenInputRef = React.useRef<HTMLInputElement>(null);
    const maskedInputRef = React.useRef<HTMLInputElement>(null);
    React.useImperativeHandle(
      forwardedRef,
      () => ref.current as HTMLInputElement,
    );

    const dateCompleteRef = React.useRef(false);

    const [date, setDate] = React.useState<Date | null | undefined>(selected);

    const { setNativeValue } = useNativeInput(name ?? '', 'text');

    React.useEffect(() => {
      if (value) {
        if (hiddenInputRef.current) {
          hiddenInputRef.current.value = value;
        }

        setDate(parseDateFromString(value));
      }
    }, [value]);

    return (
      <div>
        <input
          type="text"
          style={{
            opacity: 0,
            width: 0,
            height: 0,
            overflow: 'hidden',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
          aria-hidden
          name={name}
          tabIndex={-1}
          onChange={e => {
            onInputChange?.(e);
          }}
          ref={hiddenInputRef}
          onBlur={onBlur}
        />
        <DatePicker
          wrapperClassName="ds-wrapper"
          calendarClassName="ds-calendar"
          selected={date}
          isClearable={!props.disabled}
          showMonthDropdown
          showYearDropdown
          scrollableYearDropdown
          yearDropdownItemNumber={props?.yearDropdownItemNumber ?? 100}
          maxDate={props?.maxDate}
          minDate={props?.minDate}
          customInput={
            <Input
              name={name}
              id={name}
              label={label}
              mask="mm / dd / YYYY"
              ref={maskedInputRef}
              blocks={{
                mm: {
                  mask: IMask.MaskedRange,
                  from: 1,
                  to: 12,
                },
                dd: {
                  mask: IMask.MaskedRange,
                  from: 1,
                  to: 31,
                },
                YYYY: {
                  mask: IMask.MaskedRange,
                  from: 1900,
                  to: 9999,
                },
              }}
              trailingIcon={
                <Icon
                  type="calendar"
                  size="large"
                  color="var(--color_neutral-light)"
                />
              }
              state={state}
              stateText={stateText}
              stateDefaultIcon={stateDefaultIcon}
              onAccept={(_, mask) => {
                dateCompleteRef.current = mask.masked.isComplete;
              }}
            />
          }
          onChange={(date, e) => {
            if (date === null) {
              setDate(null);
              setNativeValue('');
              onChange?.(date, e);
            } else if (
              e &&
              (e.type === 'click' ||
                (e.type === 'change' && dateCompleteRef.current))
            ) {
              const formattedDate = date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              });

              const [month, day, year] = formattedDate.split('/');

              const fixedDate = `${year}-${month}-${day}`;

              setNativeValue(fixedDate);

              const correctedDate = new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                0,
                0,
                0,
                0,
              );

              setDate(correctedDate);

              onChange?.(correctedDate, e);
            }
          }}
          onBlur={() => {
            if (hiddenInputRef.current) {
              hiddenInputRef.current.focus();
              hiddenInputRef.current.blur();
            }
          }}
          {...props}
        />
      </div>
    );
  },
);

export { DateSelector };
